import { useState, useEffect } from 'react';
import { FoodfluenceLocation } from '@foodfluence/shared';
import { getLocationsForClient } from '../services/api/clients';

interface UseClientLocationsResult {
  locations: FoodfluenceLocation[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching locations for a client
 * @param clientId - The client ID to fetch locations for
 * @param enabled - Whether to automatically fetch locations (default: true)
 */
export const useClientLocations = (
  clientId: string | undefined,
  enabled: boolean = true
): UseClientLocationsResult => {
  const [locations, setLocations] = useState<FoodfluenceLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLocations = async () => {
    if (!clientId) {
      setLocations([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getLocationsForClient(clientId);

      if (response.success) {
        setLocations(response.data);
      } else {
        setError(response.error || 'Failed to fetch client locations');
        setLocations([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) {
      setLocations([]);
      setLoading(false);
      setError(null);
      return;
    }

    fetchLocations();
  }, [clientId, enabled]);

  const refetch = async () => {
    await fetchLocations();
  };

  return { locations, loading, error, refetch };
}; 